import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Calendar, AlertCircle } from 'lucide-react';
import { Card, Loading } from '../components/ui';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { dashboardService } from '../services/api';
import { formatMoney } from '../utils/formatters';
import toast from 'react-hot-toast';

const TransparenciaFinancieraPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardService.getDatos();
            console.log('Datos recibidos:', response.data);
            setData(response.data.data);
        } catch (error) {
            console.error('Error completo:', error);
            console.error('Response:', error.response);
            const errorMsg = error.response?.status === 403
                ? 'No tienes permisos para ver esta información'
                : error.response?.status === 401
                    ? 'Tu sesión ha expirado, por favor inicia sesión nuevamente'
                    : error.response?.data?.message || 'Error al cargar datos. Por favor, intenta nuevamente.';
            setError(errorMsg);
            toast.error('Error al cargar datos financieros');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loading size="lg" text="Cargando información financiera..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                        <TrendingUp className="text-blue-400" size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Transparencia Financiera</h1>
                        <p className="text-slate-400">Consulta de información financiera del edificio</p>
                    </div>
                </div>

                <Card className="p-6 bg-rose-500/10 border border-rose-500/30">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="text-rose-400 flex-shrink-0 mt-1" size={20} />
                        <div>
                            <h3 className="text-lg font-semibold text-rose-300 mb-2">Acceso Restringido</h3>
                            <p className="text-sm text-rose-200 mb-4">
                                {error}
                            </p>
                            <p className="text-sm text-rose-200">
                                Por favor, contacta con la administración para solicitar acceso a esta información.
                            </p>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (!data) {
        return <div className="text-white">No hay datos disponibles</div>;
    }

    const { graficos, resumen } = data;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                    <TrendingUp className="text-blue-400" size={24} />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-white">Transparencia Financiera</h1>
                    <p className="text-slate-400">Consulta de información financiera del edificio</p>
                </div>
            </div>

            {/* Resumen Financiero */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-500/20">
                            <DollarSign className="text-emerald-400" size={20} />
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Saldo Disponible</p>
                            <p className="text-xl font-bold text-emerald-400">{formatMoney(resumen.saldoDisponible)}</p>
                        </div>
                    </div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-500/20">
                            <TrendingUp className="text-blue-400" size={20} />
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Ingresos del Mes</p>
                            <p className="text-xl font-bold text-blue-400">{formatMoney(resumen.totalIngresos)}</p>
                        </div>
                    </div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-rose-500/20">
                            <TrendingUp className="text-rose-400" size={20} />
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Egresos del Mes</p>
                            <p className="text-xl font-bold text-rose-400">{formatMoney(resumen.totalEgresos)}</p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Flujo de Caja */}
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Calendar size={18} className="text-blue-400" />
                    Flujo de Caja - Últimos 6 Meses
                </h3>
                {graficos?.flujoCaja && graficos.flujoCaja.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={graficos.flujoCaja}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="mes" stroke="#94a3b8" />
                            <YAxis stroke="#94a3b8" />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                                labelStyle={{ color: '#f1f5f9' }}
                                formatter={(value) => formatMoney(value)}
                            />
                            <Legend />
                            <Line type="monotone" dataKey="ingresos" stroke="#10b981" strokeWidth={2} name="Ingresos" />
                            <Line type="monotone" dataKey="egresos" stroke="#f43f5e" strokeWidth={2} name="Egresos" />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <p className="text-slate-500 text-center py-8">No hay datos de flujo de caja</p>
                )}
            </Card>

            {/* Distribución de Egresos */}
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Distribución de Egresos del Mes</h3>
                {graficos?.distribucionEgresos && graficos.distribucionEgresos.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={graficos.distribucionEgresos}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="categoria" stroke="#94a3b8" />
                            <YAxis stroke="#94a3b8" />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                                labelStyle={{ color: '#f1f5f9' }}
                                formatter={(value) => formatMoney(value)}
                            />
                            <Bar dataKey="monto" fill="#8b5cf6" name="Monto" />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <p className="text-slate-500 text-center py-8">No hay datos de egresos</p>
                )}
            </Card>

            {/* Detalle de Ingresos del Mes */}
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Detalle de Ingresos del Mes</h3>
                {resumen?.detalleIngresos && resumen.detalleIngresos.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-700">
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Fecha</th>
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Concepto</th>
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Categoría</th>
                                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-300">Monto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resumen.detalleIngresos.map((ingreso, index) => (
                                    <tr key={index} className="border-b border-slate-800 hover:bg-slate-800/50">
                                        <td className="py-3 px-4 text-sm text-slate-400">{ingreso.fecha}</td>
                                        <td className="py-3 px-4 text-sm text-white">{ingreso.concepto}</td>
                                        <td className="py-3 px-4 text-sm text-slate-400">{ingreso.categoria}</td>
                                        <td className="py-3 px-4 text-sm text-right text-emerald-400 font-medium">{formatMoney(ingreso.monto)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-slate-700">
                                    <td colSpan="3" className="py-3 px-4 text-sm font-semibold text-white text-right">Total Ingresos:</td>
                                    <td className="py-3 px-4 text-sm text-right text-emerald-400 font-bold">{formatMoney(resumen.totalIngresos)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ) : (
                    <p className="text-slate-500 text-center py-8">No hay ingresos registrados este mes</p>
                )}
            </Card>

            {/* Detalle de Egresos del Mes */}
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Detalle de Egresos del Mes</h3>
                {resumen?.detalleEgresos && resumen.detalleEgresos.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-700">
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Fecha</th>
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Concepto</th>
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Categoría</th>
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Proveedor</th>
                                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-300">Monto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resumen.detalleEgresos.map((egreso, index) => (
                                    <tr key={index} className="border-b border-slate-800 hover:bg-slate-800/50">
                                        <td className="py-3 px-4 text-sm text-slate-400">{egreso.fecha}</td>
                                        <td className="py-3 px-4 text-sm text-white">{egreso.concepto}</td>
                                        <td className="py-3 px-4 text-sm text-slate-400">{egreso.categoria}</td>
                                        <td className="py-3 px-4 text-sm text-slate-400">{egreso.proveedor || '-'}</td>
                                        <td className="py-3 px-4 text-sm text-right text-rose-400 font-medium">{formatMoney(egreso.monto)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-slate-700">
                                    <td colSpan="4" className="py-3 px-4 text-sm font-semibold text-white text-right">Total Egresos:</td>
                                    <td className="py-3 px-4 text-sm text-right text-rose-400 font-bold">{formatMoney(resumen.totalEgresos)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ) : (
                    <p className="text-slate-500 text-center py-8">No hay egresos registrados este mes</p>
                )}
            </Card>

            {/* Nota informativa */}
            <Card className="p-4 bg-blue-500/10 border border-blue-500/30">
                <p className="text-sm text-blue-300">
                    ℹ️ Esta información es de solo consulta. Para más detalles o reportes completos, contacta con la administración.
                </p>
            </Card>
        </div>
    );
};

export default TransparenciaFinancieraPage;
